import FamilyImage from '@/assets/Onboarding/FamilyImage.png';
import MeImage from '@/assets/Onboarding/MeImage.png';
import { scrollToTop } from '@/util/scroll';
import { useDispatch, useSelector } from 'react-redux';
import { setInterlocuter } from '@/reducers/graph';
import { useInjectInterlocuter } from '@/hooks/useInjectInterlocuter';
import { useQuestionnaireNavigation } from '@/context/NavigationProvider';
import { PatientPageLayout } from '@/components/PatientPageLayout';
import { RootState } from '@/types/graph';
import { useEffect } from 'react';

const options = [
  {
    id: 'OTHER',
    image: FamilyImage,
    imageAlt: 'Pour un proche',
    label: 'Pour un proche',
  },
  {
    id: 'SELF',
    image: MeImage,
    imageAlt: 'Pour moi',
    label: 'Pour moi',
  },
] as const;

export const DEFAULT_SELF_IDENTIFICATION_TITLE = [
  'Pour qui',
  'préparez-vous',
  'cette consultation ?',
] as const;

/**
 * Reformulations de la question par parcours d'onboarding. Tout parcours absent
 * de cette table garde la formulation par défaut.
 *
 * Le PROM ARS se remplit APRÈS la consultation : demander pour qui on
 * « prépare cette consultation » n'y a aucun sens (ALDO-2742).
 *
 * La divergence vit ici, dans la copie, et non dans une étape dédiée par
 * variant comme l'accueil en a une. Rien ne diffère du comportement : mêmes
 * options, mêmes images, même injection du Locuteur — seuls trois mots
 * changent. Et une étape dédiée coûterait cher en pièges : l'id
 * `self-identification` est comparé littéralement dans
 * `STEPS_TO_SKIP_WHEN_ASSISTED`, `skipSelfIdentificationForMinors` et
 * `skipSelfIdentificationWhenExplicit`, dont les deux derniers ne passent pas
 * par `resolveBaseStepId`. Un id propre au variant y désactiverait
 * silencieusement les sauts « moins de 16 ans » et « ?self= ».
 */
export const SELF_IDENTIFICATION_TITLE_BY_ONBOARDING: Record<
  string,
  readonly string[]
> = {
  PROM_ARS: ['Pour qui', 'remplissez-vous', 'ce questionnaire ?'],
};

export const SelfIdentificationQuestion = () => {
  const { navigateNext } = useQuestionnaireNavigation();
  const dispatch = useDispatch();
  const injectInterlocuter = useInjectInterlocuter();

  const patientOnboarding = useSelector(
    (state: RootState) => state.questionnaire.config?.patientOnboarding,
  );

  const titleLines =
    (patientOnboarding &&
      SELF_IDENTIFICATION_TITLE_BY_ONBOARDING[patientOnboarding]) ||
    DEFAULT_SELF_IDENTIFICATION_TITLE;

  useEffect(() => {
    scrollToTop();
  }, []);

  const handleValidateClick = (self: 'SELF' | 'OTHER') => {
    dispatch(setInterlocuter(self));
    injectInterlocuter(self !== 'OTHER');
    navigateNext();
  };

  return (
    <PatientPageLayout
      title={
        <div className="flex w-full flex-col items-center gap-2 nxs:text-center">
          <h1 className="text-2xl min-[23rem]:text-3.5xl font-semibold leading-tight text-indigo-900 w-full">
            {titleLines.map((line, index) => (
              <span key={line}>
                {index > 0 && <br />}
                {line}
              </span>
            ))}
          </h1>
          <p className="text-sm font-light text-indigo-900 w-full">
            Les questions s’adaptent selon la personne <br />
            pour proposer un accompagnement sur mesure
          </p>
        </div>
      }
      footer={<></>}
    >
      <div className="flex flex-col items-center justify-center w-full py-4 ">
        <div className="flex w-full max-w-full gap-4 nsm:flex-row">
          {options.map(({ id, image, imageAlt, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => handleValidateClick(id)}
              className="cursor-pointer flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-3 rounded-1.5xl bg-white pb-3 pt-0 transition-opacity hover:bg-blue-50 hover:opacity-90 disabled:pointer-events-none disabled:opacity-60 overflow-hidden"
            >
              <img
                src={image}
                alt={imageAlt}
                className="h-full w-full min-w-0 flex-1 object-cover object-center px-2 pt-2 rounded-2xl"
              />
              <span className="w-full px-2 text-center text-md font-medium leading-[1.2] text-indigo-900">
                {label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </PatientPageLayout>
  );
};
